import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

// Safe arithmetic evaluator: supports + − × ÷ with standard precedence.
// Rejects anything that isn't digits, dot, whitespace, or the four operators.
function safeEval(expr) {
  const clean = expr.replace(/×/g, "*").replace(/÷/g, "/").replace(/−/g, "-");
  if (!/^[0-9+\-*/.\s]+$/.test(clean)) throw new Error("bad chars");
  const tokens = clean.match(/(\d+\.?\d*|[+\-*/])/g);
  if (!tokens || tokens.length === 0) throw new Error("empty");
  // Shunting: multiply/divide first, then add/subtract
  const nums = [parseFloat(tokens[0])];
  const ops = [];
  if (isNaN(nums[0])) throw new Error("bad num");
  for (let i = 1; i < tokens.length; i += 2) {
    const op = tokens[i];
    const n = parseFloat(tokens[i + 1]);
    if (!"+-*/".includes(op) || isNaN(n)) throw new Error("bad token");
    if (op === "*" || op === "/") {
      const a = nums.pop();
      nums.push(op === "*" ? a * n : a / n);
    } else {
      ops.push(op);
      nums.push(n);
    }
  }
  let result = nums[0];
  for (let i = 0; i < ops.length; i++) {
    result = ops[i] === "+" ? result + nums[i + 1] : result - nums[i + 1];
  }
  return result;
}

export default function Calculator() {
  const nav = useNavigate();
  const { user } = useAuth();
  const code = user?.disguise_code || "=blink";
  const [display, setDisplay] = useState("0");
  const [hidden, setHidden] = useState("");

  const press = (k) => {
    if (k === "C") { setDisplay("0"); setHidden(""); return; }
    if (k === "=") {
      // Disguise unlock: match the code (with leading '=' stripped) against typed chars
      const phrase = code.replace(/^=/, "").toLowerCase();
      if (phrase && hidden.toLowerCase().includes(phrase)) {
        nav("/scan");
        return;
      }
      try {
        const r = safeEval(display);
        setDisplay(Number.isFinite(r) ? String(r) : "Err");
      } catch { setDisplay("Err"); }
      setHidden("");
      return;
    }
    setHidden((h) => (h + k).slice(-12));
    setDisplay(d => (d === "0" || d === "Err") ? k : d + k);
  };

  // Layout: standard 4-column calc; letters "b","l","i","n","k" render as spy-mode keys
  const grid = [
    ["C","÷","×","−"],
    ["7","8","9","+"],
    ["4","5","6","="],
    ["1","2","3","b"],
    ["0",".","l","i","n","k"],
  ];

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="disguise-calculator">
      <div className="flex-1 flex items-end justify-end p-8">
        <div className="text-6xl font-light truncate" data-testid="calc-display">{display}</div>
      </div>
      <div className="grid grid-cols-4 gap-2 p-3 bg-black">
        {grid.flat().map((k, i) => (
          <button
            key={`${k}-${i}`}
            data-testid={`calc-key-${k}`}
            onClick={() => press(k)}
            className={`h-16 rounded-2xl font-medium text-2xl ${
              ["÷","×","−","+","="].includes(k) ? "bg-orange-500" :
              k === "C" ? "bg-zinc-500" : "bg-zinc-800"
            }`}
          >{k}</button>
        ))}
      </div>
      <p className="text-zinc-700 text-[10px] text-center pb-2">Type "{code}" to unlock</p>
    </div>
  );
}
